<template>
  <el-dialog
    :title="$t('sc.gallery.uploadTitle')"
    :visible="visible"
    width="520px"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="upload-dialog-content">
      <div
        class="upload-drop-zone"
        @click="triggerFileSelect"
        @drop.prevent="handleDrop"
        @dragover.prevent
      >
        <div class="upload-icon">
          <i class="el-icon-upload" />
        </div>
        <el-button size="small" plain>
          {{ $t('sc.gallery.selectFile') }}
        </el-button>
        <input
          ref="fileInput"
          type="file"
          :accept="acceptTypes"
          multiple
          class="hidden-input"
          @change="handleFileChange"
        >
      </div>

      <div
        v-if="previewFiles.length > 0"
        class="upload-preview"
      >
        <h4>{{ $t('sc.gallery.uploadPreview') }}</h4>
        <div
          v-for="(file, index) in previewFiles"
          :key="index"
          class="preview-item"
        >
          <div class="preview-thumb">
            <img
              v-if="file.isImage"
              :src="file.previewUrl"
              :alt="file.name"
            >
            <div
              v-else
              class="preview-video"
            >
              <i class="el-icon-video-camera" />
            </div>
          </div>
          <div class="preview-info">
            <div class="preview-name">
              {{ file.name }}
            </div>
            <div class="preview-size">
              {{ formatFileSize(file.size) }}
            </div>
            <el-progress
              v-if="file.status === 'uploading'"
              :percentage="file.progress"
              :stroke-width="4"
              :show-text="false"
            />
            <span
              v-if="file.status === 'success'"
              class="upload-status-success"
            >
              <i class="el-icon-check" />
            </span>
            <span
              v-if="file.status === 'error'"
              class="upload-status-error"
            >
              <i class="el-icon-close" />
            </span>
          </div>
          <i
            v-if="!file.status"
            class="el-icon-close preview-remove"
            @click="removePreviewFile(index)"
          />
        </div>
      </div>

      <div class="upload-folder-select">
        <span class="folder-label">{{ $t('sc.gallery.selectFolder') }}</span>
        <el-cascader
          v-model="selectedDirectoryId"
          :placeholder="$t('sc.gallery.pleaseSelect')"
          :options="cleanedDirectoryTree"
          :props="cascaderProps"
          clearable
          style="width: 100%"
        />
      </div>
    </div>

    <template #footer>
      <el-button
        type="primary"
        :loading="uploading"
        :disabled="previewFiles.length === 0"
        @click="handleUpload"
      >
        {{ $t('sc.gallery.confirm') }}
      </el-button>
      <el-button
        :disabled="uploading"
        @click="handleClose"
      >
        {{ $t('sc.gallery.cancel') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import SparkMD5 from 'spark-md5'
import { batchUploadCheck, batchConfirmUpload } from '_h/gallery'
import {
  GALLERY_MAX_UPLOAD_BATCH,
  GALLERY_IMAGE_ACCEPT_TYPES,
  GALLERY_VIDEO_ACCEPT_TYPES,
  GALLERY_UPLOAD_ACCEPT_STRING
} from '_c/Gallery/constants'

export default {
  name: 'UploadDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    directoryTree: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      previewFiles: [],
      selectedDirectoryId: '',
      uploading: false,
      acceptTypes: GALLERY_UPLOAD_ACCEPT_STRING
    }
  },
  computed: {
    cascaderProps() {
      return {
        value: 'id',
        label: 'name',
        children: 'children',
        emitPath: false,
        checkStrictly: true
      }
    },
    cleanedDirectoryTree() {
      const clean = (nodes) => {
        return nodes.map(node => {
          const item = { id: node.id, name: node.name }
          if (node.children?.length > 0) {
            item.children = clean(node.children)
          }
          return item
        })
      }
      return clean(this.directoryTree)
    }
  },
  methods: {
    triggerFileSelect() {
      this.$refs.fileInput.click()
    },

    handleFileChange(e) {
      const files = Array.from(e.target.files)
      this.processFiles(files)
      this.$refs.fileInput.value = ''
    },

    handleDrop(e) {
      const files = Array.from(e.dataTransfer.files)
      this.processFiles(files)
    },

    // 按图片白名单（JPG/PNG/JPEG）/ 视频列表（mp4/avi/mov）拆分有效与非法文件
    validateFiles(files) {
      const valid = []
      const invalid = []
      files.forEach(file => {
        const type = (file.type || '').toLowerCase()
        if (type.startsWith('image/')) {
          if (GALLERY_IMAGE_ACCEPT_TYPES.includes(type)) {
            valid.push(file)
          } else {
            invalid.push(file)
          }
        } else if (type.startsWith('video/')) {
          if (GALLERY_VIDEO_ACCEPT_TYPES.includes(type)) {
            valid.push(file)
          } else {
            invalid.push(file)
          }
        } else {
          invalid.push(file)
        }
      })
      return { valid, invalid }
    },

    processFiles(files) {
      if (!files || files.length === 0) return

      // 1. 单批数量上限：已在预览队列 + 本次新增 > 50 时整批拦截
      if (this.previewFiles.length + files.length > GALLERY_MAX_UPLOAD_BATCH) {
        this.$message.warning(
          this.$t('sc.gallery.uploadBatchLimitExceeded', { max: GALLERY_MAX_UPLOAD_BATCH })
        )
        return
      }

      // 2. 格式白名单：非法格式聚合提示，避免大量逐条弹窗
      const { valid, invalid } = this.validateFiles(files)
      if (invalid.length > 0) {
        const names = invalid.map(f => f.name).join('、')
        this.$message.warning(
          this.$t('sc.gallery.uploadFormatNotSupported', { names })
        )
      }
      if (valid.length === 0) return

      valid.forEach(file => {
        const isImage = file.type.startsWith('image/')
        const previewUrl = isImage ? URL.createObjectURL(file) : ''
        this.previewFiles.push({
          raw: file,
          name: file.name,
          size: file.size,
          type: file.type,
          isImage,
          previewUrl,
          md5: '',
          width: 0,
          height: 0,
          status: null,
          progress: 0
        })
      })
    },

    removePreviewFile(index) {
      const file = this.previewFiles[index]
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
      this.previewFiles.splice(index, 1)
    },

    computeFileMD5(file) {
      return new Promise((resolve, reject) => {
        const chunkSize = 2 * 1024 * 1024
        const chunks = Math.ceil(file.size / chunkSize)
        const spark = new SparkMD5.ArrayBuffer()
        const reader = new FileReader()
        let currentChunk = 0

        reader.onload = (e) => {
          spark.append(e.target.result)
          currentChunk++
          if (currentChunk < chunks) {
            loadNext()
          } else {
            resolve(spark.end())
          }
        }
        reader.onerror = reject

        const loadNext = () => {
          const start = currentChunk * chunkSize
          const end = Math.min(start + chunkSize, file.size)
          reader.readAsArrayBuffer(file.slice(start, end))
        }
        loadNext()
      })
    },

    getImageDimensions(file) {
      return new Promise((resolve) => {
        if (!file.type.startsWith('image/')) {
          resolve({ width: 0, height: 0 })
          return
        }
        const img = new Image()
        const url = URL.createObjectURL(file)
        img.onload = () => {
          URL.revokeObjectURL(url)
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve({ width: 0, height: 0 })
        }
        img.src = url
      })
    },

    async handleUpload() {
      if (this.previewFiles.length === 0) return

      this.uploading = true
      try {
        const filesPayload = await Promise.all(
          this.previewFiles.map(async (pf) => {
            const [md5, dimensions] = await Promise.all([
              this.computeFileMD5(pf.raw),
              this.getImageDimensions(pf.raw)
            ])
            pf.md5 = md5
            pf.width = dimensions.width
            pf.height = dimensions.height

            return {
              filename: pf.name,
              fileSize: (pf.size / 1024).toFixed(0),
              fileType: pf.isImage ? 'image' : 'video',
              md5,
              width: dimensions.width,
              height: dimensions.height
            }
          })
        )

        const params = { files: filesPayload }
        if (this.selectedDirectoryId) {
          params.directoryId = this.selectedDirectoryId
        }
        const data = await batchUploadCheck(this.$axios, params)
        const results = data?.results || []

        const uploadPromises = results.map((result, index) => {
          const pf = this.previewFiles[index]
          if (!pf || !result.uploadUrl) return Promise.resolve()

          pf.status = 'uploading'
          pf.progress = 0
          pf.fileId = result.fileId

          return this.$axios({
            method: 'PUT',
            url: result.uploadUrl,
            data: pf.raw,
            headers: {
              ...result.headers,
              'Content-Type': pf.type
            },
            withCredentials: 0,
            timeout: 1000 * 120,
            onUploadProgress: (e) => {
              if (e.total > 0) {
                pf.progress = Math.round((e.loaded / e.total) * 100)
              }
            }
          }).then(() => {
            pf.status = 'success'
            pf.progress = 100
          }).catch((err) => {
            console.error(`upload file ${pf.name} error:`, err)
            pf.status = 'error'
          })
        })

        await Promise.all(uploadPromises)

        const successFileIds = this.previewFiles
          .filter(f => f.status === 'success' && f.fileId)
          .map(f => f.fileId)

        if (successFileIds.length > 0) {
          await batchConfirmUpload(this.$axios, { fileIds: successFileIds })
        }

        const successCount = successFileIds.length
        const failCount = this.previewFiles.filter(f => f.status === 'error').length

        if (failCount === 0) {
          this.$message.success(this.$t('sc.gallery.uploadSuccess'))
        } else {
          this.$message.warning(`${successCount} ${this.$t('sc.gallery.uploadSuccess')}，${failCount} ${this.$t('sc.gallery.uploadFailed')}`)
        }

        this.$emit('uploaded')
        if (failCount === 0) {
          this.handleClose()
        }
      } catch (error) {
        console.error('upload error:', error)
        this.$message.error(this.$t('sc.gallery.uploadFailed'))
      } finally {
        this.uploading = false
      }
    },

    handleClose() {
      this.previewFiles.forEach(file => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })
      this.previewFiles = []
      this.selectedDirectoryId = ''
      this.$emit('update:visible', false)
    },

    formatFileSize(size) {
      if (!size) return ''
      if (size < 1024) return `${size}B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}kb`
      return `${(size / 1024 / 1024).toFixed(1)}MB`
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-dialog-content {
  .upload-drop-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    border: 1px dashed #d9d9d9;
    border-radius: 4px;
    cursor: pointer;
    transition: border-color 0.3s;

    &:hover {
      border-color: #dd3131;
    }

    .upload-icon {
      margin-bottom: 16px;

      i {
        font-size: 48px;
        color: #dd3131;
      }
    }

    .hidden-input {
      display: none;
    }
  }

  .upload-preview {
    margin-top: 20px;
    max-height: 300px;
    overflow-y: auto;

    h4 {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 12px;
    }

    .preview-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px;
      border-bottom: 1px solid #f0f0f0;

      .preview-thumb {
        width: 60px;
        height: 60px;
        flex-shrink: 0;
        border-radius: 4px;
        overflow: hidden;
        background: #f5f5f5;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .preview-video {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1a1a2e;

          i {
            font-size: 24px;
            color: #fff;
          }
        }
      }

      .preview-info {
        flex: 1;
        min-width: 0;

        .preview-name {
          font-size: 13px;
          color: #333;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .preview-size {
          font-size: 12px;
          color: #999;
          margin-top: 4px;
        }

        .el-progress {
          margin-top: 6px;
        }

        .upload-status-success {
          color: #67c23a;
          font-size: 14px;
        }

        .upload-status-error {
          color: #f56c6c;
          font-size: 14px;
        }
      }

      .preview-remove {
        cursor: pointer;
        font-size: 16px;
        color: #999;

        &:hover {
          color: #dd3131;
        }
      }
    }
  }

  .upload-folder-select {
    margin-top: 20px;

    .folder-label {
      display: block;
      font-size: 13px;
      color: #666;
      margin-bottom: 8px;
    }
  }
}
</style>
